/**
 * DISC GERAL — 20 PERGUNTAS (o questionário oficial do RH da SERCAMP).
 *
 * Aqui fica só o que PODE ser visto: o enunciado das 20 questões, as quatro
 * alternativas de cada uma, os nomes/definições dos fatores, as faixas e os
 * textos de leitura do resultado. Fica em `src/` porque as duas pontas usam o
 * mesmo texto — a página pública mostra as perguntas e o servidor as devolve —,
 * como já acontece com o questionário de clima (src/clima-modelo.ts) e com o
 * formulário oficial de avaliação (src/avaliacao-modelos.ts).
 *
 * ⚠️ O GABARITO NÃO ESTÁ NESTE ARQUIVO. Ele mora em server/disc-gabarito.ts,
 * fora do alcance do navegador: o front é um bundle só e o servidor entrega o
 * mesmo JavaScript na página pública /disc/<token>, então qualquer coisa que
 * esteja aqui pode ser lida pelo candidato no "ver código-fonte". Com o fator de
 * cada letra à mão, dava para fabricar o perfil que a vaga pede. Quem corrige é
 * o servidor.
 *
 * Fonte: "DISC GERAL — 20 PERGUNTAS" (documento impresso do RH). Nada aqui foi
 * ajustado ou "melhorado" — o texto é o do papel, para o RH conferir com o
 * formulário que continua sendo aplicado presencialmente.
 */

/** Os quatro fatores do DISC. */
export type Fator = "D" | "I" | "S" | "C";

export const FATORES: Fator[] = ["D", "I", "S", "C"];

export interface FatorInfo {
  fator: Fator;
  /** "Dominância" */
  nome: string;
  /** A definição do papel do RH. */
  descricao: string;
  /** Cor da barra no gráfico (mesma paleta do portal). */
  cor: string;
}

/** Definições do "Sistema de pontuação" — texto do gabarito oficial. */
export const FATOR_INFO: Record<Fator, FatorInfo> = {
  D: {
    fator: "D",
    nome: "Dominância",
    descricao: "iniciativa, decisão, competitividade e foco em resultados.",
    cor: "#d1495b",
  },
  I: {
    fator: "I",
    nome: "Influência",
    descricao: "comunicação, persuasão, sociabilidade e entusiasmo.",
    cor: "#e9a13b",
  },
  S: {
    fator: "S",
    nome: "Estabilidade",
    descricao: "paciência, colaboração, constância e equilíbrio.",
    cor: "#0c7a3f",
  },
  C: {
    fator: "C",
    nome: "Conformidade",
    descricao: "organização, análise, precisão, regras e qualidade.",
    cor: "#2a6f97",
  },
};

/** Instrução ao participante (o texto do papel, palavra por palavra). */
export const INSTRUCAO_DISC =
  "Em cada questão, escolha apenas uma alternativa, aquela que mais se aproxima de como você " +
  "normalmente age no trabalho e no dia a dia. Não existe resposta certa ou errada. Responda " +
  "pensando no seu comportamento natural, e não no comportamento que você acredita ser o ideal.";

/** O aviso do gabarito — sai impresso em todo resultado. */
export const AVISO_DISC =
  "O DISC deve ser utilizado como ferramenta de análise comportamental e não como único critério de contratação.";

export type Letra = "A" | "B" | "C" | "D";

export const LETRAS: Letra[] = ["A", "B", "C", "D"];

export interface Alternativa {
  letra: Letra;
  texto: string;
}

export interface QuestaoDisc {
  /** 1 a 20 (o número impresso no papel). */
  n: number;
  enunciado: string;
  alternativas: Alternativa[];
}

/**
 * As 20 questões do papel, na ordem impressa.
 *
 * A ORDEM DAS ALTERNATIVAS (A, B, C, D) é a do formulário e não pode ser
 * embaralhada: o RH digita na tela o cartão que o candidato respondeu à mão e
 * confere letra por letra com o impresso. Qual fator cada letra soma está no
 * gabarito do servidor (server/disc-gabarito.ts).
 */
export const QUESTOES_DISC: QuestaoDisc[] = [
  {
    n: 1,
    enunciado: "Quando surge um problema inesperado no trabalho, você normalmente:",
    alternativas: [
      { letra: "A", texto: "Procura entender todos os detalhes antes de agir." },
      { letra: "B", texto: "Conversa com as pessoas envolvidas para encontrar uma solução." },
      { letra: "C", texto: "Assume a situação e busca resolver rapidamente." },
      { letra: "D", texto: "Mantém a calma e procura uma solução que não gere conflitos." },
    ],
  },
  {
    n: 2,
    enunciado: "Em um trabalho em equipe, você costuma:",
    alternativas: [
      { letra: "A", texto: "Incentivar as pessoas e manter o grupo motivado." },
      { letra: "B", texto: "Observar os detalhes e garantir que tudo seja feito corretamente." },
      { letra: "C", texto: "Assumir a liderança quando percebe que é necessário." },
      { letra: "D", texto: "Ajudar a equipe e manter um ambiente harmonioso." },
    ],
  },
  {
    n: 3,
    enunciado: "Quando recebe uma tarefa nova:",
    alternativas: [
      { letra: "A", texto: "Quer entender exatamente o que precisa ser feito." },
      { letra: "B", texto: "Já pensa em como começar e entregar rapidamente." },
      { letra: "C", texto: "Procura conversar com alguém para trocar ideias." },
      { letra: "D", texto: "Prefere receber orientação e seguir gradualmente." },
    ],
  },
  {
    n: 4,
    enunciado: "Quando precisa tomar uma decisão importante:",
    alternativas: [
      { letra: "A", texto: "Analisa riscos, informações e consequências." },
      { letra: "B", texto: "Considera o impacto da decisão sobre as pessoas." },
      { letra: "C", texto: "Decide rapidamente e assume a responsabilidade." },
      { letra: "D", texto: "Prefere avaliar com calma antes de decidir." },
    ],
  },
  {
    n: 5,
    enunciado: "Quando alguém discorda da sua opinião:",
    alternativas: [
      { letra: "A", texto: "Procura argumentar e convencer a pessoa." },
      { letra: "B", texto: "Analisa se existem informações que possam comprovar sua posição." },
      { letra: "C", texto: "Mantém a tranquilidade e procura chegar a um entendimento." },
      { letra: "D", texto: "Defende sua posição de maneira firme e objetiva." },
    ],
  },
  {
    n: 6,
    enunciado: "Em relação a mudanças:",
    alternativas: [
      { letra: "A", texto: "Gosta de novidades e costuma se adaptar rapidamente." },
      { letra: "B", texto: "Prefere entender bem a mudança antes de aceitá-la." },
      { letra: "C", texto: "Aceita mudanças, mas prefere que aconteçam de maneira planejada." },
      { letra: "D", texto: "Vê a mudança como uma oportunidade para alcançar resultados diferentes." },
    ],
  },
  {
    n: 7,
    enunciado: "Quando tem uma meta para cumprir:",
    alternativas: [
      { letra: "A", texto: "Concentra-se em atingir o resultado, mesmo diante de dificuldades." },
      { letra: "B", texto: "Organiza as etapas para garantir que tudo seja realizado corretamente." },
      { letra: "C", texto: "Procura envolver outras pessoas e manter o entusiasmo." },
      { letra: "D", texto: "Trabalha de maneira constante até concluir o objetivo." },
    ],
  },
  {
    n: 8,
    enunciado: "As pessoas costumam procurar você quando precisam de:",
    alternativas: [
      { letra: "A", texto: "Organização e atenção aos detalhes." },
      { letra: "B", texto: "Motivação, comunicação ou apoio para convencer alguém." },
      { letra: "C", texto: "Uma decisão rápida ou uma solução objetiva." },
      { letra: "D", texto: "Alguém paciente para ouvir e ajudar." },
    ],
  },
  {
    n: 9,
    enunciado: "Quando precisa trabalhar sob pressão:",
    alternativas: [
      { letra: "A", texto: "Mantém a calma e continua seguindo seu ritmo." },
      { letra: "B", texto: "Assume o controle e procura resolver o que for mais urgente." },
      { letra: "C", texto: "Procura conversar e dividir as dificuldades com outras pessoas." },
      { letra: "D", texto: "Organiza as prioridades para evitar erros." },
    ],
  },
  {
    n: 10,
    enunciado: "Quando recebe uma crítica:",
    alternativas: [
      { letra: "A", texto: "Analisa se a crítica tem fundamento e o que pode ser melhorado." },
      { letra: "B", texto: "Procura conversar para entender melhor o ponto de vista da pessoa." },
      { letra: "C", texto: "Aceita e procura melhorar, mantendo a tranquilidade." },
      { letra: "D", texto: "Responde de forma direta e procura resolver o problema rapidamente." },
    ],
  },
  {
    n: 11,
    enunciado: "Em uma reunião, você geralmente:",
    alternativas: [
      { letra: "A", texto: "Participa bastante e compartilha suas ideias." },
      { letra: "B", texto: "Fala principalmente quando tem informações importantes e precisas." },
      { letra: "C", texto: "Procura ouvir todos antes de se posicionar." },
      { letra: "D", texto: "Vai direto ao ponto e busca definir decisões." },
    ],
  },
  {
    n: 12,
    enunciado: "Quando percebe que um colega está tendo dificuldades:",
    alternativas: [
      { letra: "A", texto: "Oferece ajuda e procura entender o que está acontecendo." },
      { letra: "B", texto: "Mostra como fazer de maneira mais rápida e eficiente." },
      { letra: "C", texto: "Procura conversar, incentivar e levantar o ânimo da pessoa." },
      { letra: "D", texto: "Orienta com detalhes para evitar que o problema volte a acontecer." },
    ],
  },
  {
    n: 13,
    enunciado: "Qual dessas situações mais incomoda você?",
    alternativas: [
      { letra: "A", texto: "Falta de organização e erros repetidos." },
      { letra: "B", texto: "Lentidão para tomar decisões." },
      { letra: "C", texto: "Ambiente muito fechado, sem comunicação." },
      { letra: "D", texto: "Conflitos constantes e falta de colaboração." },
    ],
  },
  {
    n: 14,
    enunciado: "Quando recebe uma responsabilidade maior:",
    alternativas: [
      { letra: "A", texto: "Fica motivado com o desafio e quer mostrar resultado." },
      { letra: "B", texto: "Procura entender todos os procedimentos envolvidos." },
      { letra: "C", texto: "Gosta da oportunidade de trabalhar com mais pessoas e influência." },
      { letra: "D", texto: "Assume a responsabilidade, mas prefere ter estabilidade e clareza." },
    ],
  },
  {
    n: 15,
    enunciado: "Quando precisa convencer alguém sobre uma ideia:",
    alternativas: [
      { letra: "A", texto: "Apresenta argumentos objetivos e resultados esperados." },
      { letra: "B", texto: "Procura criar conexão e entusiasmo com a pessoa." },
      { letra: "C", texto: "Explica cuidadosamente todos os fatos e informações." },
      { letra: "D", texto: "Escuta primeiro e procura encontrar pontos de concordância." },
    ],
  },
  {
    n: 16,
    enunciado: "Seu ambiente de trabalho ideal seria:",
    alternativas: [
      { letra: "A", texto: "Dinâmico, com desafios e autonomia." },
      { letra: "B", texto: "Organizado, com regras e processos claros." },
      { letra: "C", texto: "Alegre, comunicativo e com interação entre as pessoas." },
      { letra: "D", texto: "Estável, cooperativo e com bom relacionamento." },
    ],
  },
  {
    n: 17,
    enunciado: "Quando você percebe que algo está sendo feito de maneira errada:",
    alternativas: [
      { letra: "A", texto: "Intervém imediatamente para corrigir." },
      { letra: "B", texto: "Procura entender a causa e verificar os detalhes." },
      { letra: "C", texto: "Conversa com a pessoa para orientá-la." },
      { letra: "D", texto: "Procura corrigir sem criar desconforto ou conflito." },
    ],
  },
  {
    n: 18,
    enunciado: "Quando precisa aprender algo novo:",
    alternativas: [
      { letra: "A", texto: "Aprende fazendo e testando na prática." },
      { letra: "B", texto: "Gosta de conversar com pessoas que já conhecem o assunto." },
      { letra: "C", texto: "Prefere aprender passo a passo." },
      { letra: "D", texto: "Gosta de estudar e entender profundamente antes de executar." },
    ],
  },
  {
    n: 19,
    enunciado: "Quando recebe várias tarefas ao mesmo tempo:",
    alternativas: [
      { letra: "A", texto: "Define prioridades e parte para a execução." },
      { letra: "B", texto: "Organiza tudo e verifica prazos e detalhes." },
      { letra: "C", texto: "Conversa com os envolvidos para alinhar o que é mais importante." },
      { letra: "D", texto: "Mantém o ritmo e executa uma tarefa de cada vez." },
    ],
  },
  {
    n: 20,
    enunciado: "Qual dessas características mais representa você?",
    alternativas: [
      { letra: "A", texto: "Determinação." },
      { letra: "B", texto: "Precisão." },
      { letra: "C", texto: "Comunicação." },
      { letra: "D", texto: "Paciência." },
    ],
  },
];

/** Quantas questões o teste tem (o formulário só fecha com todas). */
export const TOTAL_QUESTOES = QUESTOES_DISC.length;

/* ------------------------------------------------------------------ *
 *  QUESTIONÁRIO POR CARGO (o que a IA escreve para cada vaga)          *
 * ------------------------------------------------------------------ *
 * O questionário acima é o GERAL: serve para qualquer vaga e continua
 * sendo o padrão de toda aplicação que não montar o seu. A partir da
 * versão "DISC por cargo", cada aplicação pode guardar as SUAS questões
 * — escritas pela IA a partir da função e editadas pelo RH —, gravadas
 * em disc_aplicacoes.questoes (ver server/disc.ts).
 *
 * A diferença de forma para o questionário geral é UMA: aqui cada
 * alternativa carrega o FATOR que ela soma. No geral isso está numa
 * tabela à parte (server/disc-gabarito.ts) porque as 20 questões são as
 * mesmas para todo mundo; um questionário escrito na hora não tem tabela
 * nenhuma, então o gabarito só pode viajar junto da alternativa.
 *
 * ⚠️ ISSO NÃO CHEGA AO CANDIDATO. A rota pública remonta cada questão sem
 * o campo `fator` (ver questoesPublicas, em server/disc-questoes.ts). Só a
 * área do RH — com login — recebe o fator, porque é ele que precisa saber
 * o que cada alternativa soma para poder editar o texto sem estragar a
 * correção. É o mesmo papel do gabarito impresso, que também é do RH.
 */

/** Uma alternativa do questionário por cargo: o texto E o fator que ele soma. */
export interface AlternativaGabarito extends Alternativa {
  fator: Fator;
}

/** Uma questão do questionário por cargo (com gabarito embutido). */
export interface QuestaoDiscGabarito {
  n: number;
  enunciado: string;
  alternativas: AlternativaGabarito[];
}

/**
 * De onde vieram as questões da aplicação — o RH lê isto na tela e o laudo
 * imprime junto, para ninguém confundir um teste feito sob medida com o
 * questionário geral que a empresa aplica no papel.
 */
export type OrigemQuestoes = "Geral" | "IA" | "Manual";

/** Quantas questões valem para esta aplicação (o geral quando ela não tem as suas). */
export function totalDeQuestoes(questoes?: { n: number }[] | null): number {
  return questoes && questoes.length > 0 ? questoes.length : TOTAL_QUESTOES;
}

/* ------------------------------------------------------------------ *
 *  Faixas e formato do resultado (a correção em si é do servidor)      *
 * ------------------------------------------------------------------ */

export type NivelFaixa = "baixa" | "moderada" | "alta" | "muito alta";

/**
 * Faixas do papel: 0–5 baixa | 6–9 moderada | 10–12 alta | 13–20 muito alta.
 *
 * O `total` existe por causa do questionário POR CARGO: quando o teste tem um
 * número de questões diferente das 20 do papel, os cortes fixos mentiriam — 6
 * marcações em 10 questões é maioria, e sairia no laudo como "moderada". Os
 * limites viram então a MESMA proporção do impresso (25%, 45% e 60% do total),
 * que em 20 questões devolve exatamente 5, 9 e 12: nenhum laudo já emitido muda
 * de faixa por causa desta conta.
 */
export function faixaDisc(valor: number, total: number = TOTAL_QUESTOES): NivelFaixa {
  const base = total > 0 ? total : TOTAL_QUESTOES;
  if (valor <= Math.round(base * 0.25)) return "baixa";
  if (valor <= Math.round(base * 0.45)) return "moderada";
  if (valor <= Math.round(base * 0.6)) return "alta";
  return "muito alta";
}

/** Uma resposta do candidato: a questão e a letra que ele marcou. */
export interface RespostaDisc {
  q: number;
  alternativa: Letra;
}

export interface PlacarDisc {
  D: number;
  I: number;
  S: number;
  C: number;
}

export interface ResultadoDisc {
  placar: PlacarDisc;
  /** Fator com mais pontos. */
  primario: Fator;
  /** Segundo fator (null quando ninguém mais pontuou). */
  secundario: Fator | null;
  /** "C/S" — como o RH escreve no laudo. */
  perfil: string;
  /** "Conformidade com Estabilidade". */
  perfilNome: string;
  /** Quantas das 20 questões foram respondidas (o resto entra como branco). */
  respondidas: number;
}

/* ------------------------------------------------------------------ *
 *  Leitura do resultado (o texto que o RH lê no laudo)                *
 * ------------------------------------------------------------------ */

/** Características de quem pontua ALTO em cada fator. */
export const CARACTERISTICAS_ALTO: Record<Fator, string[]> = {
  D: [
    "Toma decisões com rapidez e assume a responsabilidade por elas",
    "Foco em resultado, mesmo diante de dificuldades",
    "Postura firme e objetiva; vai direto ao ponto",
    "Gosta de autonomia, desafio e ritmo acelerado",
    "Intervém logo quando vê algo sendo feito de maneira errada",
  ],
  I: [
    "Comunicativo, cria conexão com facilidade",
    "Motiva e mantém o ânimo da equipe",
    "Boa capacidade de convencer e negociar",
    "Adapta-se rápido a novidades e a gente nova",
    "Prefere ambiente aberto, com conversa e interação",
  ],
  S: [
    "Paciente, constante e previsível no ritmo de trabalho",
    "Colaborativo — ajuda o colega e evita atrito",
    "Mantém a calma sob pressão",
    "Boa escuta; procura o entendimento antes do confronto",
    "Prefere mudanças planejadas, com orientação clara",
  ],
  C: [
    "Muito atento a regras, procedimentos e normas",
    "Cuidadoso e criterioso; confere antes de agir",
    "Boa capacidade de concentração e de análise",
    "Organiza prioridades e prazos para evitar erros",
    "Busca segurança, previsibilidade e qualidade no que entrega",
  ],
};

/** O que observar quando o fator vem BAIXO (0–5). */
export const ATENCAO_BAIXO: Record<Fator, string> = {
  D: "D baixo: pode indicar menor tendência a tomar decisões rápidas ou a assumir uma postura firme diante de pressão. Vale avaliar na entrevista como a pessoa reage quando precisa interromper algo errado ou inseguro, mesmo diante da pressão de colegas ou superiores.",
  I: "I baixo: perfil mais reservado e pouco expansivo. Precisa ser avaliado se a comunicação com a equipe acontece de forma clara nas situações que exigem alinhamento — passagem de serviço, manobras, atendimento e momentos de risco.",
  S: "S baixo: menor tolerância à rotina e à repetição; pode se impacientar com processos lentos ou com trabalho que exige constância. Avaliar a permanência em funções muito estáveis e a reação a esperas.",
  C: "C baixo: menor apego a detalhe, procedimento e registro. Avaliar a aderência a checklists, normas e documentação — principalmente em funções com requisito de segurança ou de qualidade.",
};

/** Como cada fator ALTO costuma pesar no dia a dia (limitação do ponto forte). */
export const ATENCAO_ALTO: Record<Fator, string> = {
  D: "D muito alto: firmeza que pode ser lida como impaciência ou atropelo. Observar como conduz divergência com pares e superiores.",
  I: "I muito alto: entusiasmo e conversa em excesso podem tirar foco de tarefas longas e detalhistas. Observar a disciplina com registro e prazo.",
  S: "S muito alto: pode demorar a se posicionar em conflito e a absorver mudanças rápidas. Observar a reação a troca de escala, de equipe e de prioridade.",
  C: "C muito alto: rigor que pode virar lentidão para decidir sem todas as informações. Observar como age quando falta dado e a decisão é urgente.",
};

/**
 * Monta a leitura padrão do resultado (o miolo do laudo).
 *
 * É determinística de propósito: sai igual todas as vezes, sem depender de
 * internet nem de chave de IA. O parecer da IA (quando o RH pede) ENTRA POR
 * CIMA disto, num campo próprio — ver server/rh-ia.ts.
 */
export function leituraDisc(
  resultado: ResultadoDisc,
  /** Quantas questões o teste tinha — ver faixaDisc (questionário por cargo). */
  total: number = TOTAL_QUESTOES,
): {
  caracteristicas: string[];
  atencao: string[];
} {
  const { placar, primario, secundario } = resultado;
  const caracteristicas = [...CARACTERISTICAS_ALTO[primario]];
  if (secundario && faixaDisc(placar[secundario], total) !== "baixa") {
    // Só entra quando o segundo fator tem força real (faixa moderada para
    // cima); senão o laudo descreveria um traço que o teste não sustenta.
    caracteristicas.push(...CARACTERISTICAS_ALTO[secundario].slice(0, 2));
  }

  const atencao: string[] = [];
  for (const f of FATORES) {
    if (faixaDisc(placar[f], total) === "baixa") atencao.push(ATENCAO_BAIXO[f]);
  }
  if (faixaDisc(placar[primario], total) === "muito alta") atencao.push(ATENCAO_ALTO[primario]);

  return { caracteristicas, atencao };
}
